import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Checkbox, Input, Radio, Spin, message } from 'antd';
import { AllState } from 'constants/common';
import usePayerOptions from 'hooks/credentialing/usePayerOptions';
import { useGroupCredentialingEnrollment } from 'hooks/credentialing/useGroupCredentialingEnrollment';
import useCredentialProviders from 'hooks/credentialing/useCredentialProviders';
import {
    getCredentialingProviderStates,
    saveProviderPriorCredentialing,
    saveProviderPanels,
    getProviderPriorCredentialing,
    getGroupProviderPayers,
    getPayerRequirements,
    savePayerRequirements,
    getCredBasicInfo,
    saveIrsLetter,
    saveCredPayoutAccount,
} from 'api/credentialing';
import type {
    IrsLetterDto,
    PayerRequirementsDto,
    ProviderCredentialingPayoutAccountDto,
    TAddressPrefill,
    TPayerChainContext,
    TSelectedPayer,
} from 'components/CredentialingPayerSteps';
import { buildPayoutDto } from 'components/CredentialingPayoutForm';
import { W9_FIRST_PAGE_HINT } from 'components/W9UploadDesc';
import {
    EPayerStep,
    GovProgramStep,
    ServiceAddressStep,
    OptumW9Step,
    AetnaFocusStep,
    IrsLetterStep,
    MedicarePayoutStep,
    Np104Step,
    NP104_TITLE,
    NP104_SUBTITLE,
    isNp104BlockedPayer,
    firstSubmitContext,
    UhcGroupNotice,
    loadPayerChainData,
    getApplicablePayerSteps,
    selectedStates,
    additionalW9ItemValid,
    seedAdditionalW9,
    mergeSeededW9,
} from 'components/CredentialingPayerSteps';
import {
    OrgCredentialingPayerRef,
    TKlarityPayer,
    ProviderCredentialingPriorCredentialingDto,
    ProviderCredentialingPayerVo,
} from 'types/credentialing';
import PayerUnavailableTag, { isPayerUnavailable } from 'components/CredentialingModal/components/PayerUnavailableTag';
import s from './s.module.less';

export type NpiHistory = '' | 'no' | 'direct';
export type NpiNoReason = '' | 'never' | 'company' | 'hospital';

export interface ProviderPayerResult {
    npiHistory: NpiHistory;
    npiNoReason: NpiNoReason;
    // Payers the provider is already credentialed with under their own NPI (step 1, "Yes" path).
    alreadyCredentialed: OrgCredentialingPayerRef[];
    notSure: boolean;
    otherPayers: string;
    // Payers selected to credential for on this practice (step 2).
    selectedPayers: OrgCredentialingPayerRef[];
}

interface ProviderPayerModalProps {
    open: boolean;
    providerId: number;
    providerName: string;
    onClose: () => void;
    onComplete: () => void;
}

const getStateName = (code: string) => AllState.find((i) => i.dictKey === code)?.content || code;

// Stable identity for a payer within a state — payers can repeat across states.
const payerKey = (payer: Pick<TKlarityPayer, 'id' | 'state'>) => `${payer.id}-${payer.state}`;

const toRef = (payer: TKlarityPayer): OrgCredentialingPayerRef => ({
    klarityPayerId: payer.id,
    payerName: payer.name,
    state: payer.state,
    highDemand: payer.highDemand,
});

// `label` is what gets saved to priorNoReason; `code` is the enum the API contract documents.
// Hydration accepts either so rows written by any client still map back to a radio option.
const NO_REASONS: { value: NpiNoReason; code: string; label: string }[] = [
    { value: 'never', code: 'NEVER', label: 'Never credentialed with anyone' },
    {
        value: 'company',
        code: 'VIA_COMPANY',
        label: 'Credentialed through a company like Headway, Alma, Grow Therapy, or SonderMind, under their contracts',
    },
    { value: 'hospital', code: 'VIA_HOSPITAL', label: 'Credentialed with a hospital or another group practice' },
];

// Two-step provider payer modal opened from the group flow for a specific provider.
// Step 1: capture the provider's prior NPI credentialing history (already-active panels).
// Step 2: pick the payers to credential for now, excluding the panels they already hold.
// States come from the provider id; payers come from those states.
const ProviderPayerModal: React.FC<ProviderPayerModalProps> = ({
    open,
    providerId,
    providerName,
    onClose,
    onComplete,
}) => {
    // 1=prior NPI 问题；2=payer 选择；3=payer 触发的条件步骤链（V2：Gov → Service address → Optum W-9 → Aetna Focus）
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [states, setStates] = useState<string[]>([]);
    const [statesLoading, setStatesLoading] = useState(false);
    const [hydrating, setHydrating] = useState(false);
    const [savingStep1, setSavingStep1] = useState(false);
    const [savingStep2, setSavingStep2] = useState(false);

    // True once hydration has restored a saved step-2 selection, which then owns `selectedKeys` —
    // the inherited-payer seeding below must not clobber it when `payers` resolves later.
    const step2HydratedRef = useRef(false);

    // V2 链状态：payer-requirements 工作副本（水合 + 会话内编辑）、当前链与位置
    const [payerReqs, setPayerReqs] = useState<PayerRequirementsDto>({});
    const [chainSteps, setChainSteps] = useState<EPayerStep[]>([]);
    // 算链用的上下文：104 答 No 后按新选择重算剩下的步骤
    const chainCtxRef = useRef<TPayerChainContext | undefined>(undefined);
    const [chainPos, setChainPos] = useState(0);
    const [chainSaving, setChainSaving] = useState(false);
    // 该 provider 自己 Basic information 的 contactAddress（Service address 只预填同州州块）
    const [addressPrefill, setAddressPrefill] = useState<TAddressPrefill | undefined>(undefined);
    // IRS letter（provider 级）/ payout（practice 级一个账户）走各自接口，算链时随触发上下文一起水合
    const [irsLetter, setIrsLetter] = useState<IrsLetterDto>({});
    const [payout, setPayout] = useState<ProviderCredentialingPayoutAccountDto>({});
    // Medicaid 注册问卷的上下文：该 provider 的实体类型 + 预填依据
    const [medicaidCtx, setMedicaidCtx] = useState<{
        entityType?: string;
        licenseActionNo: boolean;
        payoutOnFile: boolean;
    }>({ licenseActionNo: false, payoutOnFile: false });

    // Step 1 state
    const [npiHistory, setNpiHistory] = useState<NpiHistory>('');
    const [npiNoReason, setNpiNoReason] = useState<NpiNoReason>('');
    const [activeState1, setActiveState1] = useState('');
    const [alreadyKeys, setAlreadyKeys] = useState<Set<string>>(new Set());
    const [notSure, setNotSure] = useState(false);
    const [otherChecked, setOtherChecked] = useState(false);
    const [otherPayers, setOtherPayers] = useState('');

    // Step 2 state
    const [activeState2, setActiveState2] = useState('');
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

    // #19：按这个 provider 自己的执照判断哪些 payer 不可选
    const [payers, payersLoading, refetchPayers] = usePayerOptions(states, states.length > 0, providerId);
    const multi = states.length > 1;

    // The practice-level payers every provider inherits by default (pre-checked in step 2).
    const [enrollment] = useGroupCredentialingEnrollment();
    const inheritedPayerIds = useMemo(() => (enrollment?.payers || []).map((p) => p.klarityPayerId), [enrollment]);

    // 本次提交的 roster（provider 数），驱动 step2 的 UHC <20 人蓝色通知
    const [rosterProviders] = useCredentialProviders();

    // Fetch the provider's states from their id when the modal opens.
    useEffect(() => {
        if (!open || !providerId) return;
        let cancelled = false;
        const fetchStates = async () => {
            setStatesLoading(true);
            try {
                const res = await getCredentialingProviderStates([providerId]);
                const list = res?.data?.data;
                if (!cancelled && Array.isArray(list)) {
                    setStates(list);
                }
            } catch (err) {
                console.error('Failed to fetch provider states:', err);
            } finally {
                if (!cancelled) setStatesLoading(false);
            }
        };
        fetchStates();
        return () => {
            cancelled = true;
        };
    }, [open, providerId]);

    // Reset the wizard whenever it (re)opens or retargets, so hydration below starts from a
    // clean slate rather than the previous provider's answers.
    useEffect(() => {
        if (!open) return;
        setStep(1);
        setNpiHistory('');
        setNpiNoReason('');
        setAlreadyKeys(new Set());
        setNotSure(false);
        setOtherChecked(false);
        setOtherPayers('');
        setSelectedKeys(new Set());
        step2HydratedRef.current = false;
        // V2 链状态随 provider 切换/重开一并重置，避免 A provider 的数据串到 B
        setPayerReqs({});
        setChainSteps([]);
        setChainPos(0);
        setAddressPrefill(undefined);
        setIrsLetter({});
        setPayout({});
    }, [open, providerId]);

    // Restore the provider's previously saved answers (the edit scenario). Both responses carry
    // (payerId, state) pairs directly, so this doesn't have to wait for `payers` to load.
    useEffect(() => {
        if (!open || !providerId) return undefined;
        let cancelled = false;
        const hydrate = async () => {
            setHydrating(true);
            try {
                const [priorRes, payersRes, reqsRes, basicRes] = await Promise.all([
                    getProviderPriorCredentialing(providerId),
                    getGroupProviderPayers([providerId]),
                    // V2：payer-requirements 与该 provider 的 contactAddress 在同一水合里恢复，
                    // 否则 owner 重开已完成 provider 时四步链会当作没填过、每次重弹
                    getPayerRequirements(providerId),
                    getCredBasicInfo(providerId),
                ]);
                if (cancelled) return;

                const savedReqs: PayerRequirementsDto | undefined = reqsRes?.data?.data;
                if (savedReqs) setPayerReqs(savedReqs);
                // additionalW9 尚无 primary：从该 provider 的 Verification 种一条（仅首次；
                // 之后与 Verification 各自独立），深链注入的 other 条目不受影响
                seedAdditionalW9(savedReqs?.additionalW9, providerId).then((seeded) => {
                    if (cancelled || !seeded) return;
                    setPayerReqs((prev) => ({ ...prev, additionalW9: mergeSeededW9(prev.additionalW9, seeded) }));
                });
                const contactAddr = basicRes?.data?.data?.contactAddress;
                if (contactAddr) {
                    setAddressPrefill({
                        street: contactAddr.practiceAddress,
                        city: contactAddr.practiceCity,
                        zip: contactAddr.practiceZip,
                        state: contactAddr.practiceState,
                    });
                }

                // Step 1 · prior credentialing history
                const prior: ProviderCredentialingPriorCredentialingDto | undefined = priorRes?.data?.data;
                if (prior && typeof prior.priorCredentialed === 'boolean') {
                    if (prior.priorCredentialed) {
                        setNpiHistory('direct');
                        const yes = prior.priorYesPayers;
                        setAlreadyKeys(
                            new Set(
                                (yes?.byState || []).flatMap((st) =>
                                    (st.payers || []).map((payerId) => `${payerId}-${st.state}`),
                                ),
                            ),
                        );
                        setNotSure(!!yes?.notSure);
                        const other = yes?.otherPayers || '';
                        setOtherChecked(!!other);
                        setOtherPayers(other);
                    } else {
                        setNpiHistory('no');
                        const saved = prior.priorNoReason || '';
                        setNpiNoReason(NO_REASONS.find((r) => r.code === saved || r.label === saved)?.value || '');
                    }
                }

                // Step 2 · saved panel selection takes priority; falling through leaves the
                // inherited-payer seeding below to supply the default.
                const rows: ProviderCredentialingPayerVo[] = payersRes?.data?.data || [];
                const savedRows = rows.filter((r) => r.providerId === providerId);
                if (savedRows.length) {
                    setSelectedKeys(new Set(savedRows.map((r) => `${r.klarityPayerId}-${r.payerState}`)));
                    step2HydratedRef.current = true;
                }
            } catch (err) {
                console.error('Failed to load saved provider payer answers:', err);
            } finally {
                if (!cancelled) setHydrating(false);
            }
        };
        hydrate();
        return () => {
            cancelled = true;
        };
    }, [open, providerId]);

    // Default the active state tab (both steps) once states arrive.
    useEffect(() => {
        setActiveState1(states[0] || '');
        setActiveState2(states[0] || '');
    }, [states]);

    // Pre-select the practice-inherited payers in step 2, skipping ones already credentialed.
    // Only for a provider with no saved selection — hydration owns selectedKeys otherwise, and
    // may resolve either side of this effect.
    useEffect(() => {
        if (!payers.length || step2HydratedRef.current) return;
        const inherited = new Set(inheritedPayerIds);
        setSelectedKeys(
            new Set(payers.filter((p) => inherited.has(p.id) && !alreadyKeys.has(payerKey(p))).map(payerKey)),
        );
        // Only seed on payer/inherited load — user toggles thereafter own selectedKeys.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payers, inheritedPayerIds]);

    // Panels marked already-credentialed in step 1 are excluded from step 2. Enforced here rather
    // than only at seed time, since hydration can set alreadyKeys after the seeding above ran.
    useEffect(() => {
        setSelectedKeys((prev) => {
            const next = new Set(Array.from(prev).filter((k) => !alreadyKeys.has(k)));
            return next.size === prev.size ? prev : next;
        });
    }, [alreadyKeys]);

    // 执照不符（#19）的 payer 不能留在选择里：继承的 practice payer 预勾、104 答 No 后重新拉列表都会碰到
    useEffect(() => {
        setSelectedKeys((prev) => {
            const next = new Set(
                Array.from(prev).filter((k) => !isPayerUnavailable(payers.find((p) => payerKey(p) === k))),
            );
            return next.size === prev.size ? prev : next;
        });
    }, [payers]);

    const statePayers1 = useMemo(
        () => payers.filter((p) => p.state === activeState1).filter((p) => p.status === 'ACTIVE'),
        [payers, activeState1],
    );
    const statePayers2 = useMemo(
        () => payers.filter((p) => p.state === activeState2).filter((p) => p.status === 'ACTIVE'),
        [payers, activeState2],
    );

    const excludedPayerIds = useMemo(() => {
        const excluded = new Set<string>();
        for (const key of [...selectedKeys, ...alreadyKeys]) {
            const payer = payers.find((p) => payerKey(p) === key);
            if (payer?.exclusivePayerIds) {
                payer.exclusivePayerIds.split(',').forEach((id) => excluded.add(id.trim()));
            }
        }
        return excluded;
    }, [selectedKeys, alreadyKeys, payers]);

    const loading = statesLoading || payersLoading || hydrating;

    const toggleAlready = (payer: TKlarityPayer) => {
        const key = payerKey(payer);
        setAlreadyKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else {
                next.add(key);
                setNotSure(false);
            }
            return next;
        });
    };

    const toggleSelected = (payer: TKlarityPayer) => {
        const key = payerKey(payer);
        if (alreadyKeys.has(key) || isPayerUnavailable(payer)) return;
        if (excludedPayerIds.has(String(payer.id)) && !selectedKeys.has(key)) return;
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleNotSure = (checked: boolean) => {
        setNotSure(checked);
        if (checked) {
            setAlreadyKeys(new Set());
            setOtherChecked(false);
            setOtherPayers('');
        }
    };

    const handleOtherToggle = (checked: boolean) => {
        setOtherChecked(checked);
        if (checked) setNotSure(false);
        else setOtherPayers('');
    };

    const canContinueStep1 = (() => {
        if (npiHistory === 'direct') return alreadyKeys.size > 0 || notSure || otherPayers.trim().length > 0;
        if (npiHistory === 'no') return !!npiNoReason;
        return false;
    })();

    const refsFromKeys = (keys: Set<string>) => payers.filter((p) => keys.has(payerKey(p))).map(toRef);
    // 触发计算的输入：带上 payerType（Blue plan 靠它识别，IRS letter 要用）
    const selectedForTriggers = (): TSelectedPayer[] =>
        payers
            .filter((p) => selectedKeys.has(payerKey(p)))
            .map((p) => ({ payerName: p.name, state: p.state, payerType: p.payerType }));

    // Build the prior-credentialing payload from step-1 answers.
    // No path → just the reason text; Yes path → already-credentialed panels grouped by state.
    const buildPriorDto = (): ProviderCredentialingPriorCredentialingDto => {
        if (npiHistory !== 'direct') {
            return {
                providerId,
                priorCredentialed: false,
                priorNoReason: NO_REASONS.find((r) => r.value === npiNoReason)?.label || '',
            };
        }
        const byStateMap: Record<string, string[]> = {};
        refsFromKeys(alreadyKeys).forEach((ref) => {
            if (!byStateMap[ref.state]) byStateMap[ref.state] = [];
            byStateMap[ref.state].push(String(ref.klarityPayerId));
        });
        return {
            providerId,
            priorCredentialed: true,
            priorYesPayers: {
                byState: Object.entries(byStateMap).map(([state, list]) => ({ state, payers: list })),
                notSure,
                otherPayers: otherChecked ? otherPayers.trim() : '',
            },
        };
    };

    // Step 1 "Continue": persist the prior-credentialing answer, then advance to payer selection.
    const handleStep1Continue = async () => {
        setSavingStep1(true);
        try {
            const res = await saveProviderPriorCredentialing(buildPriorDto());
            if (res && !res.error) {
                setStep(2);
            } else {
                message.error(res?.error || 'Failed to save');
            }
        } finally {
            setSavingStep1(false);
        }
    };

    // Step 2 "Continue": persist the provider's panel selection as DRAFT, then run the V2
    // payer-triggered chain (Gov → Service address → Optum W-9 → Aetna Focus) before finishing.
    // 幂等：已保存完整的步骤不出现；全部完成过则直接 onComplete，与旧行为一致。
    const handleFinish = async () => {
        const selectedPayers = refsFromKeys(selectedKeys);
        setSavingStep2(true);
        try {
            const res = await saveProviderPanels({
                providerId,
                klarityPayerIds: selectedPayers.map((p) => p.klarityPayerId),
            });
            if (res && !res.error) {
                const chainData = await loadPayerChainData(providerId, true);
                // 首次提交：每一步都出，之前填过的回填再确认（IRS letter 回填本人的或 owner 的）
                const ctx = firstSubmitContext(chainData.ctx);
                chainCtxRef.current = ctx;
                setIrsLetter(chainData.irsLetter);
                setPayout(chainData.payout);
                setMedicaidCtx({
                    entityType: chainData.entityType,
                    licenseActionNo: chainData.licenseActionNo,
                    payoutOnFile: chainData.ctx.payoutOnFile,
                });
                const applicable = getApplicablePayerSteps(selectedForTriggers(), ctx);
                if (applicable.length) {
                    setChainSteps(applicable);
                    setChainPos(0);
                    setStep(3);
                } else {
                    onComplete();
                }
            } else {
                message.error(res?.error || 'Failed to save');
            }
        } finally {
            setSavingStep2(false);
        }
    };

    // 链内某步 Continue：全量覆盖保存该 provider 的 payer-requirements（OVERRIDE-PROVIDER-ID 定位），
    // 成功后进下一步；链尾 → onComplete 回 roster
    const handleChainContinue = async () => {
        setChainSaving(true);
        try {
            // 每步各存各的：IRS letter / payout 走独立接口，其余步骤全量覆盖 payer-requirements
            const current = chainSteps[chainPos];
            let res: any;
            if (current === EPayerStep.IRS_LETTER) res = await saveIrsLetter(irsLetter, providerId);
            else if (current === EPayerStep.MEDICARE_PAYOUT) res = await saveCredPayoutAccount(buildPayoutDto(payout, {}));
            else res = await savePayerRequirements(payerReqs, providerId);
            if (res && !res.error) {
                let steps = chainSteps;
                // 104 答 No（10/26）：Blue Shield of California 从这个 provider 的选择里去掉（panel 已在进链前存过，
                // 要按新选择重存一次），重新拉列表让它显示为不可选；不回到 payer 列表，按新选择继续往后（10/27 定）
                if (current === EPayerStep.NP_104 && payerReqs.caNp104 === false) {
                    const nextKeys = new Set(
                        Array.from(selectedKeys).filter((k) => {
                            const p = payers.find((x) => payerKey(x) === k);
                            return !(p && isNp104BlockedPayer({ payerName: p.name, state: p.state }));
                        }),
                    );
                    setSelectedKeys(nextKeys);
                    const saved = await saveProviderPanels({
                        providerId,
                        klarityPayerIds: refsFromKeys(nextKeys).map((r) => r.klarityPayerId),
                    });
                    if (saved?.error) {
                        message.error(saved.error || 'Failed to save');
                        return;
                    }
                    refetchPayers();
                    const remaining = getApplicablePayerSteps(
                        payers
                            .filter((x) => nextKeys.has(payerKey(x)))
                            .map((x) => ({ payerName: x.name, state: x.state, payerType: x.payerType })),
                        chainCtxRef.current ? { ...chainCtxRef.current, np104: false } : undefined,
                    );
                    steps = [
                        ...chainSteps.slice(0, chainPos + 1),
                        ...chainSteps.slice(chainPos + 1).filter((st) => remaining.includes(st)),
                    ];
                    setChainSteps(steps);
                }
                if (chainPos < steps.length - 1) {
                    setChainPos(chainPos + 1);
                } else {
                    onComplete();
                }
            } else {
                message.error(res?.error || 'Failed to save');
            }
        } finally {
            setChainSaving(false);
        }
    };

    const handleChainBack = () => {
        if (chainPos > 0) {
            setChainPos(chainPos - 1);
        } else {
            setStep(2);
        }
    };

    const renderStateChips = (active: string, onPick: (st: string) => void) =>
        multi ? (
            <div className={s.stateChips}>
                {states.map((st) => (
                    <div
                        key={st}
                        className={`${s.stateChip} ${st === active ? s.stateChipActive : ''}`}
                        onClick={() => onPick(st)}
                    >
                        {getStateName(st)}
                    </div>
                ))}
            </div>
        ) : null;

    const renderStep1 = () => (
        <>
            <div className={s.header}>
                <div className={s.title}>Has {providerName} been credentialed with any payers under their own NPI?</div>
                <div className={s.subtitle}>Answer for {providerName}. This helps us tailor their payer options.</div>
            </div>

            <div className={s.body}>
                <div className={s.optionList}>
                    <label className={`${s.optionItem} ${npiHistory === 'no' ? s.optionItemActive : ''}`}>
                        <Radio
                            checked={npiHistory === 'no'}
                            onChange={() => {
                                setNpiHistory('no');
                                setAlreadyKeys(new Set());
                                setNotSure(false);
                                setOtherChecked(false);
                                setOtherPayers('');
                            }}
                        />
                        <span>No, not yet</span>
                    </label>

                    {npiHistory === 'no' && (
                        <div className={s.subOptions}>
                            {NO_REASONS.map((r) => (
                                <label key={r.value} className={s.subOptionItem}>
                                    <Radio checked={npiNoReason === r.value} onChange={() => setNpiNoReason(r.value)} />
                                    <span>{r.label}</span>
                                </label>
                            ))}
                        </div>
                    )}

                    <label className={`${s.optionItem} ${npiHistory === 'direct' ? s.optionItemActive : ''}`}>
                        <Radio
                            checked={npiHistory === 'direct'}
                            onChange={() => {
                                setNpiHistory('direct');
                                setNpiNoReason('');
                            }}
                        />
                        <span>Yes, already credentialed directly with payers under their own NPI</span>
                    </label>
                </div>

                {npiHistory === 'direct' && (
                    <div className={s.acSection}>
                        {renderStateChips(activeState1, setActiveState1)}
                        <div className={s.sectionLabel}>
                            Which payers is {providerName} already credentialed with
                            {activeState1 ? ` in ${getStateName(activeState1)}` : ''}?
                        </div>
                        <div className={s.payerList}>
                            {statePayers1.map((p) => {
                                const checked = alreadyKeys.has(payerKey(p));
                                return (
                                    <div
                                        key={payerKey(p)}
                                        className={`${s.payer} ${checked ? s.payerSelected : ''}`}
                                        onClick={() => toggleAlready(p)}
                                    >
                                        <Checkbox checked={checked} onChange={() => toggleAlready(p)} />
                                        <span className={s.payerName}>
                                            {p.name}
                                            {p.highDemand === 1 && <span className={s.demandTag}>High demand</span>}
                                        </span>
                                    </div>
                                );
                            })}
                            <label className={s.payer}>
                                <Checkbox
                                    checked={otherChecked}
                                    onChange={(e) => handleOtherToggle(e.target.checked)}
                                />
                                <span className={s.payerName}>Other</span>
                            </label>
                            <label className={s.payer}>
                                <Checkbox checked={notSure} onChange={(e) => handleNotSure(e.target.checked)} />
                                <span className={s.payerName}>I&apos;m not sure</span>
                            </label>
                        </div>
                        {otherChecked && (
                            <Input
                                className={s.otherInput}
                                placeholder="Enter payer name(s)"
                                value={otherPayers}
                                onChange={(e) => setOtherPayers(e.target.value)}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className={s.footer}>
                <Button
                    type="primary"
                    block
                    className={s.primaryBtn}
                    disabled={!canContinueStep1}
                    loading={savingStep1}
                    onClick={handleStep1Continue}
                >
                    Continue
                </Button>
            </div>
        </>
    );

    const renderStep2 = () => (
        <>
            <div className={s.header}>
                <div className={s.title}>Select the payers for {providerName}</div>
                <div className={s.subtitle}>
                    These are inherited from the practice. Adjust them for {providerName} if needed. Each panel is a $50
                    refundable deposit, returned as cashback after their first claim. Panels they already have are
                    excluded.
                </div>
            </div>

            <div className={s.body}>
                <UhcGroupNotice
                    selectedPayerNames={refsFromKeys(selectedKeys).map((p) => p.payerName)}
                    rosterSize={rosterProviders.length}
                />
                {renderStateChips(activeState2, setActiveState2)}
                {multi && activeState2 && (
                    <div className={s.sectionLabel}>Available now in {getStateName(activeState2)}</div>
                )}
                <div className={s.payerList}>
                    {statePayers2.map((p) => {
                        const key = payerKey(p);
                        const isAC = alreadyKeys.has(key);
                        const isExcluded = excludedPayerIds.has(String(p.id)) && !selectedKeys.has(key);
                        const isUnavailable = isPayerUnavailable(p);
                        const isDisabled = isAC || isExcluded || isUnavailable;
                        const checked = selectedKeys.has(key);
                        return (
                            <div
                                key={key}
                                className={`${s.payer} ${checked ? s.payerSelected : ''} ${isDisabled ? s.payerDisabled : ''}`}
                                onClick={() => toggleSelected(p)}
                            >
                                <Checkbox checked={checked} disabled={isDisabled} onChange={() => toggleSelected(p)} />
                                <span className={s.payerName}>
                                    {p.name}
                                    {isAC && <span className={s.alreadyTag}>Already credentialed</span>}
                                    {isExcluded && p.payerType && <span className={s.alreadyTag}>Only one {p.payerType} panel is allowed</span>}
                                    {/* 执照不符时只留黄色 tag，不再叠 High demand */}
                                    {p.highDemand === 1 && !isUnavailable && <span className={s.demandTag}>High demand</span>}
                                    {isUnavailable && <PayerUnavailableTag reason={p.unavailableReason} />}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className={s.footer}>
                <Button type="primary" block className={s.primaryBtn} loading={savingStep2} onClick={handleFinish}>
                    Continue
                </Button>
                <Button block className={s.backBtn} disabled={savingStep2} onClick={() => setStep(1)}>
                    Back
                </Button>
            </div>
        </>
    );

    // V2 链步骤的标题/副标题（文案同 solo，副标题前缀点名 provider，对齐 step1 "Answer for {name}" 的模式）
    const CHAIN_META: Record<EPayerStep, { title: string; subtitle: string }> = {
        [EPayerStep.NP_104]: { title: NP104_TITLE, subtitle: NP104_SUBTITLE },
        [EPayerStep.GOV_PROGRAM]: {
            title: 'Government program details',
            subtitle:
                "Both are confirmed separately for each state they're credentialing in. Medicare issues its certification number through a regional contractor, and Medicaid is run by each state.",
        },
        [EPayerStep.IRS_LETTER]: {
            title: 'IRS Tax ID confirmation letter',
            subtitle:
                'Medicare, Medicaid, Molina and the Blue plans verify the Tax ID the practice bills under against the IRS record, and a W-9 does not count because it is self-reported.',
        },
        [EPayerStep.MEDICARE_PAYOUT]: {
            title: 'Payout account',
            subtitle:
                'The bank account where insurance reimbursements will be deposited once credentialed. Payers pay this account directly, and bank details are encrypted and never shared.',
        },
        [EPayerStep.SERVICE_ADDRESS]: {
            title: 'Service address by state',
            subtitle:
                "Payers require an address of record in every state they're applying to, including virtual visit only, used for contractual and statutory notices, not published as a place of care.",
        },
        [EPayerStep.OPTUM_W9]: {
            title: 'UnitedHealthcare: a signed W-9 for each Tax ID',
            // primary（该 provider 的 Verification 种子）完整才提"已经收过一份"
            subtitle: `${
                additionalW9ItemValid((payerReqs.additionalW9?.items || []).find((it) => it.primary))
                    ? 'UnitedHealthcare needs a signed W-9 for every Tax ID and business name (DBA) they have billed claims under, with no limit on how many. We already have one from their Verification step, so most practices just confirm it here.'
                    : 'UnitedHealthcare needs a signed W-9 for every Tax ID and business name (DBA) they have billed claims under, with no limit on how many.'
            } ${W9_FIRST_PAGE_HINT}`,
        },
        [EPayerStep.AETNA_FOCUS]: {
            title: 'Practice focus areas',
            subtitle: 'Aetna asks for up to 8 focus areas that best describe the practice.',
        },
    };

    const renderChainStep = () => {
        const current = chainSteps[chainPos];
        if (!current) return null;
        const meta = CHAIN_META[current];
        const selected = selectedForTriggers();
        return (
            <>
                <div className={s.header}>
                    <div className={s.title}>{meta.title}</div>
                    <div className={s.subtitle}>
                        Answering for {providerName}. {meta.subtitle}
                    </div>
                </div>
                {current === EPayerStep.GOV_PROGRAM && (
                    <GovProgramStep
                        states={selectedStates(selected)}
                        medicare={payerReqs.medicare || []}
                        medicaid={payerReqs.medicaid || []}
                        onChange={(medicare, medicaid) => setPayerReqs((prev) => ({ ...prev, medicare, medicaid }))}
                        management={payerReqs.medicareManagement}
                        onManagementChange={(medicareManagement) =>
                            setPayerReqs((prev) => ({ ...prev, medicareManagement }))
                        }
                        medicaidEnrollment={payerReqs.medicaidEnrollment}
                        onMedicaidEnrollmentChange={(medicaidEnrollment) =>
                            setPayerReqs((prev) => ({ ...prev, medicaidEnrollment }))
                        }
                        // 原型 mcdPath：roster 2 人以上走 rendering，只问个人那几题
                        medicaidVariant={rosterProviders.length > 1 ? 'rendering' : 'individual'}
                        entityType={medicaidCtx.entityType}
                        licenseActionNo={medicaidCtx.licenseActionNo}
                        payoutOnFile={medicaidCtx.payoutOnFile}
                        onContinue={handleChainContinue}
                        onBack={handleChainBack}
                        saving={chainSaving}
                        flush
                    />
                )}
                {current === EPayerStep.NP_104 && (
                    <Np104Step
                        value={payerReqs.caNp104}
                        onChange={(caNp104) => setPayerReqs((prev) => ({ ...prev, caNp104 }))}
                        onContinue={handleChainContinue}
                        onBack={handleChainBack}
                        saving={chainSaving}
                        flush
                    />
                )}
                {current === EPayerStep.IRS_LETTER && (
                    <IrsLetterStep
                        value={irsLetter}
                        onChange={setIrsLetter}
                        onContinue={handleChainContinue}
                        onBack={handleChainBack}
                        saving={chainSaving}
                        flush
                    />
                )}
                {current === EPayerStep.MEDICARE_PAYOUT && (
                    <MedicarePayoutStep
                        group
                        value={payout}
                        onChange={setPayout}
                        onContinue={handleChainContinue}
                        onBack={handleChainBack}
                        saving={chainSaving}
                        flush
                    />
                )}
                {current === EPayerStep.SERVICE_ADDRESS && (
                    <ServiceAddressStep
                        states={selectedStates(selected)}
                        value={payerReqs.serviceAddresses || []}
                        onChange={(serviceAddresses) => setPayerReqs((prev) => ({ ...prev, serviceAddresses }))}
                        onContinue={handleChainContinue}
                        onBack={handleChainBack}
                        saving={chainSaving}
                        flush
                        prefill={addressPrefill}
                    />
                )}
                {current === EPayerStep.OPTUM_W9 && (
                    <OptumW9Step
                        value={payerReqs.additionalW9 || {}}
                        onChange={(additionalW9) => setPayerReqs((prev) => ({ ...prev, additionalW9 }))}
                        onContinue={handleChainContinue}
                        onBack={handleChainBack}
                        saving={chainSaving}
                        flush
                    />
                )}
                {current === EPayerStep.AETNA_FOCUS && (
                    <AetnaFocusStep
                        value={payerReqs.focusAreas || []}
                        onChange={(focusAreas) => setPayerReqs((prev) => ({ ...prev, focusAreas }))}
                        onContinue={handleChainContinue}
                        onBack={handleChainBack}
                        saving={chainSaving}
                        flush
                    />
                )}
            </>
        );
    };

    const renderBody = () => {
        if (step === 1) return renderStep1();
        if (step === 2) return renderStep2();
        return renderChainStep();
    };

    return (
        <Modal open={open} onCancel={onClose} footer={null} width={880} className={s.modal} title={null} destroyOnClose>
            <Spin spinning={loading}>{renderBody()}</Spin>
        </Modal>
    );
};

export default ProviderPayerModal;
